import arrayMutators from 'final-form-arrays';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useToasts } from 'react-toast-notifications';
import Button from '../../components/Button';
import Error from '../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../components/Form';
import Header from '../../components/Header';
import Loader from '../../components/Loader';
import { CompanySettings } from '../../types/settings';
import { useAxios } from '../../utils/hooks';
import VALIDATORS from '../../utils/validators';
import { CurrentCompanyContext } from '../../hooks/CurrentCompanyContext';
import { Pill } from '../../components/Pill';
import IconGoogle from '../../components/icons/IconGoogle';

export default function Settings() {
    const axios = useAxios();
    const { addToast } = useToasts();
    const [settings, setSettings] = useState<CompanySettings>();
    const company = useContext(CurrentCompanyContext);

    useEffect(() => {
        if (!axios || !axios.current) {
            return;
        }

        axios.current
            .get<CompanySettings>('/company/settings')
            .then(({ data }) => {
                setSettings(data);
            })
            .catch((error) => {
                addToast(error.message, { appearance: 'error' });
            });
    }, [addToast, axios]);

    const handleDisconnectGoogle = useCallback(() => {
        if (window.confirm('Are you sure you want to disconnect your Google account?')) {
            axios.current?.delete<{ ok: boolean }>('/google/connect').then(() => window.location.reload());
        }
    }, [axios]);

    const handleSaveSettings = async (newSettings: CompanySettings) => {
        try {
            const response = await axios?.current?.post<CompanySettings>('/company/settings', newSettings);
            if (response?.data) {
                addToast('Settings saved!', { appearance: 'success' });
                setSettings(response.data);
            } else {
                addToast('Error saving settings', { appearance: 'error' });
            }
        } catch (err: any) {
            console.error(err);
            addToast(err?.response?.data?.error ?? 'Error saving settings', { appearance: 'error' });
        }
    };

    const googleConnected = !!company?.google;

    return (
        <div className="w-full">
            <Header>Settings</Header>
            {!settings && <Loader full />}
            {!!settings && (
                <Form<CompanySettings>
                    onSubmit={handleSaveSettings}
                    initialValues={settings}
                    mutators={{
                        ...(arrayMutators as any),
                    }}
                >
                    {({ handleSubmit, submitting, invalid }) => (
                        <StyledForm onSubmit={handleSubmit} className="w-full mt-4 shadow">
                            <FormSection>
                                <FormSectionHeader>Company</FormSectionHeader>
                                <FormSectionBody>
                                    <div className="mb-2">
                                        <label className="text-sm text-gray-600">Company Name</label>
                                        <Field name="name" component="input" type="text" validate={VALIDATORS.required}>
                                            {({ input, meta }) => (
                                                <>
                                                    <input
                                                        {...input}
                                                        placeholder="My Company Ltd"
                                                        className="input w-full"
                                                    />
                                                    {meta.error && (
                                                        <span className="ml-2">
                                                            <Error message={meta.error} />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                    <div className="flex mt-2">
                                        <Button
                                            type="submit"
                                            kind="primary"
                                            className="ml-auto"
                                            state={submitting ? 'SAVING' : 'READY'}
                                            disabled={invalid}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </FormSectionBody>
                            </FormSection>
                        </StyledForm>
                    )}
                </Form>
            )}
            {googleConnected && (
                <div className="pt-8 bg-white space-y-6 rounded-lg max-w-4xl mx-auto mb-4">
                    <FormSection>
                        <FormSectionHeader>Connected Accounts</FormSectionHeader>
                        <FormSectionBody>
                            <div className="mb-4 flex items-center space-x-2">
                                <Pill
                                    title={
                                        <>
                                            <IconGoogle className="w-5 inline" /> Google Connected
                                        </>
                                    }
                                />
                                <Button kind="outline" onClick={handleDisconnectGoogle}>
                                    Disconnect
                                </Button>
                            </div>
                        </FormSectionBody>
                    </FormSection>
                </div>
            )}
        </div>
    );
}
